import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Cloud, CloudDownload, CloudOff, CloudUpload, HardDrive, Images, Loader2, Search, Upload, X } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { MediaDetailDialog } from '@/components/media/MediaDetailDialog';
import { MediaDropZone } from '@/components/media/MediaDropZone';
import { MediaThumbnail } from '@/components/media/MediaThumbnail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMediaIndexSync } from '@/hooks/useMediaIndexSync';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useMediaPreferences } from '@/hooks/useMediaPreferences';
import { useToast } from '@/hooks/useToast';
import { formatBytes, isUploaded, type MediaItem, type MediaKind } from '@/lib/media';
import { cn } from '@/lib/utils';

type KindFilter = 'all' | MediaKind;
type UploadFilter = 'all' | 'uploaded' | 'local';

const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'pdf', label: 'PDFs' },
  { value: 'other', label: 'Other' },
];

/**
 * `/media` — the media library: everything the user has added on this
 * device, with tag/kind/upload filters, and per-item detail.
 *
 * The library is **local-first**: files are stored in this browser's
 * IndexedDB and mirrored to Blossom only when uploaded. That's not just
 * an offline nicety — Blossom's blob-listing endpoint is explicitly
 * "optional and unrecommended" per BUD-12, so a library simply cannot be
 * a live view of server contents and has to maintain its own index. Doing
 * that locally means it also works logged out and doesn't publicly
 * announce every file the user touches.
 */
export function MediaPage() {
  useSeoMeta({ title: 'Media library — Wikistar' });

  const { user } = useCurrentUser();
  const { items, isLoading, tags, upload } = useMediaLibrary();
  const { publishNip94, setPublishNip94 } = useMediaPreferences();
  const { isSyncing, syncUp, syncDown, canSync } = useMediaIndexSync();
  const { toast } = useToast();

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [uploadState, setUploadState] = useState<UploadFilter>('all');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>();
  const [bulkUploading, setBulkUploading] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (items ?? []).filter((item) => {
      if (kind !== 'all' && item.kind !== kind) return false;
      if (uploadState === 'uploaded' && !isUploaded(item)) return false;
      if (uploadState === 'local' && isUploaded(item)) return false;
      // Tag filters are AND-ed: selecting two tags narrows to items
      // carrying both, which is the useful behavior when tags are being
      // used to pin down one specific file.
      if (activeTags.length > 0 && !activeTags.every((t) => item.tags.includes(t))) return false;
      if (needle) {
        const haystack = `${item.name} ${item.alt ?? ''} ${item.tags.join(' ')}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [items, query, kind, uploadState, activeTags]);

  const localOnly = useMemo(() => (items ?? []).filter((i) => !isUploaded(i)), [items]);
  const localBytes = useMemo(
    () => (items ?? []).reduce((sum, i) => sum + (i.hasBlob ? i.size : 0), 0),
    [items],
  );

  const selectedItem = items?.find((i) => i.sha256 === selected);

  const handleUploadAll = async () => {
    setBulkUploading(true);
    let ok = 0;
    let failed = 0;
    for (const item of localOnly) {
      try {
        await upload(item.sha256);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkUploading(false);
    toast({
      title: ok > 0 ? `Uploaded ${ok} file${ok === 1 ? '' : 's'}` : "Couldn't upload",
      description: failed > 0 ? `${failed} failed — check your Blossom servers and try again.` : undefined,
      variant: ok === 0 ? 'destructive' : undefined,
    });
  };

  const handleSyncUp = async () => {
    try {
      await syncUp();
      toast({
        title: 'Media index synced',
        description: 'Your library catalog is now encrypted and backed up to Nostr. Files themselves stay local-only.',
      });
    } catch (error) {
      toast({
        title: "Couldn't sync the media index",
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    }
  };

  const handleSyncDown = async () => {
    try {
      const pulled = await syncDown();
      toast({
        title: pulled === undefined || pulled === 0 ? 'Nothing to pull' : `Pulled ${pulled} item${pulled === 1 ? '' : 's'}`,
        description:
          pulled === undefined || pulled === 0
            ? 'Your local library is already up to date, or the index is empty.'
            : 'Your media catalog is up to date. Files that aren\u2019t local will show as "local only" until you re-add them.',
      });
    } catch (error) {
      toast({
        title: "Couldn't sync the media index",
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <Images className="size-6" />
              Media
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Files stored on this device{user ? ' for this account' : ''}, ready to drop into articles. Upload to
              Blossom to make them public.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {user && canSync && (
              <>
                <Button type="button" variant="outline" size="sm" onClick={handleSyncDown} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <CloudDownload className="size-4" />}
                  Sync down
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleSyncUp} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <CloudUpload className="size-4" />}
                  Sync up
                </Button>
              </>
            )}
            {localOnly.length > 0 && user && (
              <Button type="button" onClick={handleUploadAll} disabled={bulkUploading}>
                {bulkUploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                Upload {localOnly.length} local file{localOnly.length === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </div>

        <MediaDropZone className="mb-6" />

        {(items?.length ?? 0) > 0 && (
          <>
            {/* Storage summary — a local-first library needs to be honest
                about the disk it's occupying. */}
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <HardDrive className="size-3.5" />
                {formatBytes(localBytes)} on this device
              </span>
              <span className="flex items-center gap-1.5">
                <Cloud className="size-3.5" />
                {(items?.length ?? 0) - localOnly.length} uploaded
              </span>
            {localOnly.length > 0 && (
              <span className="flex items-center gap-1.5">
                <CloudOff className="size-3.5" />
                {localOnly.length} local only
              </span>
            )}

            {/* The NIP-94 opt-in lives inline in the storage summary so
                the feature is discovered alongside the upload action it
                modifies, not buried behind a settings dialog. */}
            <button
              type="button"
              onClick={() => setPublishNip94(!publishNip94)}
              className="flex items-center gap-1.5 rounded-md hover:text-foreground"
              title={
                publishNip94
                  ? 'Uploads also publish a NIP-94 file-metadata event to Nostr, so other clients and your other devices can find them.'
                  : 'Uploads stay off-Nostr — no metadata events are published.'
              }
            >
              <Switch checked={publishNip94} aria-label="Publish uploads to Nostr (NIP-94)" className="pointer-events-none scale-[0.8]" />
              NIP-94 {publishNip94 ? 'on' : 'off'}
            </button>
            </div>

            <div className="mb-4 space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, alt text, or tag…"
                  className="pl-9"
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                {KIND_FILTERS.map((f) => (
                  <FilterChip key={f.value} active={kind === f.value} onClick={() => setKind(f.value)}>
                    {f.label}
                  </FilterChip>
                ))}
                <span className="mx-1 w-px self-stretch bg-border" aria-hidden />
                <FilterChip active={uploadState === 'uploaded'} onClick={() => setUploadState(uploadState === 'uploaded' ? 'all' : 'uploaded')}>
                  <Cloud className="size-3" />
                  Uploaded
                </FilterChip>
                <FilterChip active={uploadState === 'local'} onClick={() => setUploadState(uploadState === 'local' ? 'all' : 'local')}>
                  <CloudOff className="size-3" />
                  Local only
                </FilterChip>
              </div>

              {tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {tags.map(({ tag, count }) => (
                    <FilterChip
                      key={tag}
                      active={activeTags.includes(tag)}
                      onClick={() =>
                        setActiveTags((prev) =>
                          prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
                        )
                      }
                    >
                      {tag}
                      <span className="text-muted-foreground/60">{count}</span>
                    </FilterChip>
                  ))}
                  {activeTags.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setActiveTags([])}>
                      <X className="size-3" />
                      Clear tags
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square w-full rounded-lg" />
            ))}
          </div>
        ) : (items?.length ?? 0) === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-md text-muted-foreground">
                No media yet. Add images, video, audio, or PDFs above — they're saved on this device, so you can
                organize them offline and upload when you're ready.
              </p>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">Nothing matches those filters.</p>
            </CardContent>
          </Card>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((item) => (
              <li key={item.sha256}>
                <MediaCard item={item} onSelect={() => setSelected(item.sha256)} />
              </li>
            ))}
          </ul>
        )}

        {!user && (items?.length ?? 0) > 0 && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Log in to upload these files to your Blossom servers.
          </p>
        )}

        <MediaDetailDialog item={selectedItem} onOpenChange={(open) => !open && setSelected(undefined)} />
      </div>
    </Layout>
  );
}

function MediaCard({ item, onSelect }: { item: MediaItem; onSelect: () => void }) {
  return (
    <button type="button" onClick={onSelect} className="group w-full text-left">
      <MediaThumbnail item={item} className="transition-shadow group-hover:shadow-md" />
      <p className="mt-1.5 truncate text-xs font-medium" title={item.name}>
        {item.name}
      </p>
      <p className="truncate text-[11px] text-muted-foreground">
        {item.tags.length > 0 ? item.tags.join(', ') : formatBytes(item.size)}
      </p>
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export default MediaPage;
